import { isPlainObject, isFunction } from "lodash";

const TIMEOUT_ERROR = new Error("Timeout error");

class Fetcher {
  constructor(config) {
    this.config = {
      ...config,
      timeoutMs: 30000
    };
  }

  parseResponse(response) {
    const contentType = response.headers.get("Content-Type");
    if (contentType != null) {
      if (contentType.includes("text/plain")) {
        return response.text().catch(() => "");
      }
      if (/^(image|video)\//.test(contentType)) {
        return response.blob().catch(() => null);
      }
    }
    // Always try to decode JSON
    const { transformResponse } = this.config;
    return response
      .json()
      .then(json =>
        isFunction(transformResponse) ? transformResponse(json) : json
      )
      .catch(() => ({}));
  }

  timeout(promise, ms = this.config.timeoutMs) {
    return Promise.race([
      promise,
      new Promise((_, reject) => {
        setTimeout(() => void reject(TIMEOUT_ERROR), ms);
      })
    ]);
  }
}

["get", "post", "put", "patch", "delete"].forEach(method => {
  Fetcher.prototype[method] = async function(url, options) {
    const { timeoutMs, host = "", ...baseConfig } = this.config;

    let response;
    let status = 0;

    return new Promise((resolve, reject) => {
      this.timeout(
        fetch(
          `${host}${url}`,
          getOptions(baseConfig, {
            ...options,
            method: method.toUpperCase()
          })
        ),
        timeoutMs
      )
        .then(
          resp => {
            response = resp;
            return resp;
          },
          err => {
            // If error happens here, the request failed
            // if it is TypeError throw network error
            if (err instanceof TypeError) {
              throw new Error("Network error");
            }
            throw err;
          }
        )
        .then(this.parseResponse.bind(this))
        .then(data => {
          if (response.ok) {
            return resolve(data);
          }
          status = (response && response.status) || 0;
          return reject({
            code: status >= 500 ? "ServerError" : "ClientError",
            status,
            body: data
          });
        })
        .catch(err => {
          const baseError = {
            status,
            name: err.name,
            message: err.message
          };
          if (err instanceof Error && err.message === "Network error") {
            return reject({
              ...baseError,
              code: "NetworkError"
            });
          } else if (
            err instanceof Error &&
            err.message === TIMEOUT_ERROR.message
          ) {
            return reject({
              ...baseError,
              code: "TimeoutError"
            });
          }
          return reject({
            ...baseError
          });
        });
    });
  };
});

function getOptions(baseConfig, requestOptions) {
  const { transformRequest, ...options } = baseConfig;
  const headers = getHeaders(options, requestOptions);
  return {
    ...requestOptions,
    ...(headers ? { headers } : undefined),
    ...getBody(requestOptions, transformRequest)
  };
}

function getHeaders(baseConfig, requestOptions) {
  const { headers: baseHeaders } = baseConfig;
  const { headers: requestHeaders, method } = requestOptions;
  return {
    ...getContentTypeHeader(requestOptions.body, method),
    ...baseHeaders,
    ...requestHeaders
  };
}

function getContentTypeHeader(body, method) {
  if (typeof self !== "undefined" && body instanceof self.FormData) {
    return undefined;
  }
  if (!method || method.toLowerCase() !== "get") {
    return { "Content-Type": "application/json" };
  }
  return null;
}

function getBody(requestOptions, transformRequest) {
  const { body } = requestOptions;
  return isPlainObject(body)
    ? {
        body: JSON.stringify(
          isFunction(transformRequest) ? transformRequest(body) : body
        )
      }
    : undefined;
}

export default Fetcher;
